const jht = require('json-html-table');

/**
 * @template T
 * @param tuples An array of tuples where each tuple contains two elements of type T
 * @returns A list of unique elements from the second position of each tuple, including the first element of the first tuple
 * @example
 * const tuples = [['a', 'b'], ['b', 'c'], ['c', 'd']];
 * const result = tuplesToList(tuples);
 * console.log(result); // Output: ['a', 'b', 'c', 'd']
 */
export const tuplesToList = <T extends unknown>(tuples: [T, T][]) => {
  if (tuples.length === 0) return [];
  return tuples.reduce<T[]>(
    (acc, [_first, second]) => {
      if (!acc.includes(second)) {
        acc.push(second);
      }
      return acc;
    },
    [tuples[0][0]],
  );
};

export const getDiff = <AT extends Record<string, any>, BT extends AT>(
  a: AT,
  b: BT,
) =>
  Object.keys(a).reduce<Partial<AT>>((diff, key: keyof AT) => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal !== bVal) {
      diff[key] = aVal;
    }
    return diff;
  }, {});

export const deepEqual = (a: any, b: any): boolean => {
  if (Array.isArray(a)) {
    if (Array.isArray(b)) {
      return (
        a.length === b.length &&
        a.every((item, index) => deepEqual(item, b[index]))
      );
    }
    return false;
  }

  if (a === null || b === null || a === undefined || b === undefined) {
    return a === b;
  }

  if (typeof a === 'object' && typeof b === 'object') {
    if (Object.keys(a).length !== Object.keys(b).length) {
      return false;
    }

    return Object.keys(a).every((key) => deepEqual(a[key], b[key]));
  }

  return a === b;
};

export const toHtmlTable = (data: Object[], title: string = ''): string => {
  if (data.length === 0) {
    return '';
  }
  const headers = Object.keys(data[0]);
  const table = jht(data, headers);
  if (title !== '') {
    const columns = headers.length;
    return table.replace(
      '<table>',
      `<table><tr><th colspan="${columns}">${title}</th></tr>`,
    );
  }
  return table;
};

/**
 * @description Creates a filter function that checks if an object matches the search criteria
 * @returns function that returns true if the object matches the search criteria
 */
export const createFilter =
  <T extends Record<string, any>>(search: Record<keyof T, any>) =>
  (subject: T) =>
    Object.keys(search).every(
      (k) =>
        subject[k] === search[k] ||
        (Array.isArray(search[k]) &&
          (search[k].length === 0 || search[k].includes(subject[k]))) ||
        (typeof search[k] === 'object' &&
          +search[k].min <= subject[k] &&
          subject[k] <= +search[k].max) ||
        (typeof search[k] === 'function' && search[k](subject[k])) ||
        (typeof search[k] === 'boolean' && search[k] && subject[k]) || // for only filtering when true
        (typeof search[k] === 'boolean' && !search[k] && true),
    );
